import { Playbook } from '@/types/playbook/playbook';
import { Bot, Calendar, CheckCircle, HeadphonesIcon, Link, User } from 'lucide-react';

export const customerCheckIn: Playbook = {
  meta: {
    id: 'customer-check-in',
    slug: 'customer-check-in',
    categoryId: 'gather-insights',
    name: 'The Customer Check-in Link',
    tagline: 'Send periodic check-in genies.',
    description: 'Email voice Genie links to customers for regular check-ins. Gather feedback, identify issues, and discover upsell opportunities.',
    strategy: 'Email voice Genie links to customers for quarterly check-ins. Your Genie gathers feedback, identifies issues, and discovers upsell opportunities.',
    benefits: [
      'Proactive customer success',
      'Early issue identification',
      'Upsell opportunity discovery',
      'Improved customer retention'
    ],
    targetAudience: ['B2B SaaS', 'Subscription services', 'Professional services'],
    estimatedSetupTime: '25 minutes',
    difficulty: 'intermediate',
    tags: ['customer-success', 'check-ins', 'retention', 'upselling'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Customer Check-in Playbook - Genie Customer Success Links',
    metaDescription: 'Improve retention with Genie-powered customer check-ins. Proactive feedback collection and issue identification via email links.',
    keywords: ['customer check-in', 'customer success', 'retention strategy', 'feedback collection', 'Genie customer service']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "Hi! I'm Emma from TechCorp. Thanks for taking a moment for our quarterly check-in. We value your feedback! How has your experience been with our service lately?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! Thanks for clicking our check-in link. How's everything going with our service?" },
    { speaker: 'user', message: "Actually, we've been having some issues with the API" },
    { speaker: 'agent', message: "I'm sorry to hear that. Can you tell me more about what's happening?" },
    { speaker: 'user', message: "The response times have been slow lately" },
    { speaker: 'agent', message: "I understand. I've noted this issue and can schedule a call with our technical team. What time works best for you?" }
  ],
  templateConfig: {
    firstMessage: "Hi! Thanks for clicking our check-in link. I'm [NAME] from [COMPANY]. How's your experience been?",
    systemPrompt: "You are a customer success Genie accessed via an email link. Be genuinely interested in their experience, identify any issues or opportunities, gather feedback, and schedule follow-ups if needed.",
    tools: ['crm_integration', 'ticket_creation', 'upsell_recommendations', 'calendar_booking', 'feedback_database']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer receives check-in email',
      description: 'Regular customer gets quarterly check-in email with personalized Genie link'
    },
    {
      type: 'genie',
      icon: Link,
      genieIcon: 'genie-lamp',
      label: 'Clicks link to start conversation',
      description: 'Customer clicks link and immediately starts conversing with a dedicated Genie'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Assesses customer health',
      description: 'Genie asks about satisfaction, usage, and any challenges customer is facing'
    },
    {
      type: 'action',
      icon: HeadphonesIcon,
      genieIcon: 'settings',
      label: 'Identifies support needs',
      description: 'Uncovers issues, feature requests, or areas where customer needs help'
    },
    {
      type: 'action',
      icon: Calendar,
      genieIcon: 'calendar-check',
      label: 'Schedules appropriate follow-up',
      description: 'Books technical calls, success sessions, or escalates urgent issues to human team'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Proactive customer success',
      description: 'Issues caught early, opportunities identified, and customer satisfaction maintained'
    }
  ],
  metrics: {
    conversionRate: '56%',
    responseTime: 'Immediate',
    satisfactionScore: '4.8/5',
    costSavings: '$85k/year',
    timeToValue: '2 minutes'
  },
  testimonials: [
    {
      companyName: 'CloudFlow Systems',
      authorName: 'David Wilson',
      authorTitle: 'VP of Customer Success',
      quote: 'Check-in Genie links improved our customer retention by 18% and helped us identify $200k in expansion opportunities.',
      metrics: '18% improvement in retention'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Customer Check-in Email Templates',
      description: 'Email templates for sending Genie check-in links to customers',
      url: '/templates/check-in-emails'
    },
    {
      type: 'guide',
      title: 'Customer Success Automation',
      description: 'How to automate regular customer health checks and follow-ups',
      url: '/docs/customer-success-automation'
    }
  ]
};